import { useEffect } from 'react';

/**
 * BlogSEO — Sets document head meta tags for SEO, Open Graph, and JSON-LD.
 * Updates on mount and cleans up on unmount.
 */
export function BlogSEO({ title, description, url, type = 'website', publishedTime, modifiedTime, tags, image }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const metas = [];
    const setMeta = (property, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('article:')) {
          el.setAttribute('property', property);
        } else {
          el.setAttribute('name', property);
        }
        document.head.appendChild(el);
        metas.push(el);
      }
      el.setAttribute('content', content);
    };

    // Standard
    setMeta('description', description);

    // Open Graph
    setMeta('og:title', title);
    setMeta('og:description', description);
    setMeta('og:url', url);
    setMeta('og:type', type === 'article' ? 'article' : 'website');
    setMeta('og:site_name', 'Rentual');
    if (image) setMeta('og:image', image);

    // Article-specific
    if (type === 'article') {
      if (publishedTime) setMeta('article:published_time', publishedTime);
      if (modifiedTime) setMeta('article:modified_time', modifiedTime);
      if (tags) tags.forEach(tag => setMeta('article:tag', tag));
    }

    // Twitter
    setMeta('twitter:card', image ? 'summary_large_image' : 'summary');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // JSON-LD structured data
    let jsonLd = document.querySelector('#blog-jsonld');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.id = 'blog-jsonld';
      jsonLd.type = 'application/ld+json';
      document.head.appendChild(jsonLd);
    }

    if (type === 'article') {
      jsonLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description: description,
        url: url,
        datePublished: publishedTime,
        dateModified: modifiedTime || publishedTime,
        author: { '@type': 'Organization', name: 'Rentual', url: 'https://rentual.co' },
        publisher: {
          '@type': 'Organization',
          name: 'Rentual',
          url: 'https://rentual.co',
          logo: { '@type': 'ImageObject', url: 'https://rentual.co/logo192.png' }
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        ...(image ? { image: { '@type': 'ImageObject', url: image } } : {}),
        ...(tags ? { keywords: tags.join(', ') } : {}),
      });
    } else {
      jsonLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'Rentual Blog',
        description: description,
        url: url,
        publisher: { '@type': 'Organization', name: 'Rentual', url: 'https://rentual.co' }
      });
    }

    return () => {
      document.title = prevTitle;
      metas.forEach(el => el.remove());
      if (jsonLd) jsonLd.textContent = '';
    };
  }, [title, description, url, type, publishedTime, modifiedTime, tags, image]);

  return null;
}
